import React, { ChangeEvent, useCallback, useEffect, useState, useMemo } from 'react';
import { Formik } from 'formik';
import { useRouter } from 'next/router';
import { Button } from 'components/Button';
import { validationSchema } from 'utils/input/helpers';
import { InputField } from 'components/Formik/InputField';
import { useInputsManagerStore } from 'modules/InputsManager/components/StoreContext';
import { useUnloadConfirmation } from 'hooks/useUnloadConfirmation';
import { metadataBlock } from 'modules/InputViewer/components/InputInfo/styles';
import { flex } from 'styles/utils';
import { cx } from '@linaria/core';
import { width100perc } from 'modules/WorkflowBuilder/ModelToolbar/ModelToolbar.styled';
type MetadataProps = {
  input: CF.API.Inputs.Input;
  metadataString?: string;
  onShowMetadata?: () => void;
};
enum Mode {
  Create,
  Edit,
}
export const Metadata = ({
  input,
  metadataString = '',
  onShowMetadata
}: MetadataProps): JSX.Element => {
  const {
    userOrOrgId,
    appId
  } = (useRouter().query as {
    userOrOrgId: string;
    appId: string;
  });
  const [textValue, setTextValue] = useState(metadataString);
  const [isDirty, setIsDirty] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const {
    updateInputMetadata
  } = useInputsManagerStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSaveButtonDisabled = useMemo(() => textValue === metadataString || isSubmitting, [textValue, isSubmitting]);
  useUnloadConfirmation(isDirty);
  useEffect(() => {
    setTextValue(metadataString);
  }, [metadataString]);
  useEffect(() => {
    setIsDirty(textValue !== metadataString);
  }, [textValue, metadataString]);
  const displayForm = useCallback(() => {
    setShowForm(true);
  }, []);
  useEffect(() => {
    if (showForm && onShowMetadata) {
      onShowMetadata();
    }
  }, [showForm]);
  const onSubmitMetadata = useCallback(async (metadata: CF.API.Metadata | undefined, mode: Mode) => {
    await updateInputMetadata(input.id, metadata, appId, userOrOrgId, mode);
    setIsSubmitting(false);
  }, [input.id, userOrOrgId, appId]);
  const handleEdit = useCallback(values => {
    setIsSubmitting(true);
    const {
      metadata
    } = values;
    if (metadata.length > 0 && metadata !== metadataString) {
      const json = JSON.parse(metadata);
      onSubmitMetadata(json, Mode.Edit);
    } else {
      onSubmitMetadata(undefined, Mode.Edit);
    }
  }, [onSubmitMetadata, metadataString]);
  const handleCreate = useCallback(values => {
    setIsSubmitting(true);
    const {
      metadata
    } = values;
    if (metadata) {
      const json = JSON.parse(metadata);
      onSubmitMetadata(json, Mode.Create);
    }
  }, [onSubmitMetadata]);
  const handleTextChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setTextValue(event.target.value);
  }, []);
  const handleKeyDown = useCallback(event => {
    if (event.key.toLowerCase() === 'a' && (event.ctrlKey || event.metaKey)) {
      event.stopPropagation();
    }
  }, []);
  return <>
      {metadataString || showForm ? <Formik initialValues={{
      metadata: metadataString
    }} enableReinitialize onSubmit={!metadataString ? handleCreate : handleEdit} validationSchema={validationSchema}>
          {({
        setFieldTouched,
        submitForm,
        isValid
      }) => {
        const metadataRows = metadataString ? Math.min(10, metadataString.split('\n').length) : 5;
        return <div className={cx(flex.init, flex.direction.column, flex.gap.half, width100perc)}>
                <InputField id="metadata" name="metadata" aria-label="input-viewer-metadata" role="textbox" className={metadataBlock} rows={metadataRows} placeholder='{"key": "value"}' onInput={() => setFieldTouched('metadata', true)} isMultiLine value={textValue} onChange={handleTextChange} onKeyDown={handleKeyDown} />
                <div>
                  <Button variant={isSaveButtonDisabled || !isValid ? 'secondary' : 'primary'} onClick={submitForm} data-testid="save-changes-button" disabled={isSaveButtonDisabled || !isValid}>
                    Save Changes
                  </Button>
                </div>
              </div>;
      }}
        </Formik> : <Button variant="link" onClick={displayForm} data-testid="add-metadata-button">
          Add
        </Button>}
    </>;
};